// src/utils/cssVars.js
// Resolves CSS custom properties to their values for inline-style assignments.
// Used when JS event handlers need to write style.<prop> directly with the
// canonical token-resolved value (parallels Convention #71 pattern).
// Cached on first read; CSS vars don't change at runtime in this app.

const cache = {};

const resolve = (varName) => {
  if (cache[varName] !== undefined) return cache[varName];
  const value = getComputedStyle(document.documentElement)
    .getPropertyValue(varName)
    .trim();
  cache[varName] = value;
  return value;
};

/**
 * Get a numeric opacity value from the design-token scale.
 * @param {'faint'|'muted'|'disabled'|'soft'|'veil'} name
 * @returns {string} numeric string (e.g. "0.7")
 */
export const getOpacity = (name) => resolve(`--opacity-${name}`);

/**
 * Get a color value from the design-token scale.
 * Pass the token suffix exactly as it appears in variables.css.
 * @param {string} name — e.g. 'gold', 'sandstone', 'border', 'text-muted'
 * @returns {string} CSS color value (e.g. "#d4af74")
 */
export const getColor = (name) => resolve(`--${name}`);

/**
 * Get a numeric breakpoint value (px) from the design-system canonicals.
 *
 * Sync triplet — keep aligned across all three locations:
 *   1. src/styles/variables.css        — canonical doc block at file head
 *   2. src/utils/cssVars.js            — BREAKPOINTS const map (this file)
 *   3. scripts/audit-breakpoints.sh    — ALLOWED array
 *
 * CSS @media cannot use var(--*) (Step 15 strategy b — documented constants).
 * JS uses these constants instead. New breakpoint values must be added to all
 * three locations.
 *
 * Sibling enforcement: scripts/audit-js-tokens.sh detects regression in
 * the 14 files migrated by Bucket A Steps 14.6 + 16-17. Coverage is
 * limited to migrated files; Bucket B (1,180 deferred sites) is future
 * work.
 *
 * @param {'phone'|'mobile'|'tablet'|'narrow-desktop'|'wide-desktop'} name
 * @returns {number} pixel value (e.g. 768)
 */
const BREAKPOINTS = {
  'phone':          600,
  'mobile':         768,
  'tablet':         1024,
  'narrow-desktop': 900,
  'wide-desktop':   1100,
  'home-small-phone': 360,
  'home-wide-monitor': 2100,
};
export const getBreakpoint = (name) => BREAKPOINTS[name];
